import { useState, useEffect } from 'react';
import { Skeleton } from '@nextui-org/react';
import VarietyPriceChart from '../../../../common/src/components/charts/VarietyPriceChart';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import { MarketData } from 'common/src/utils/types';
import { getDashboardMarketToday } from 'common/src/api/market_data';
import { RMB_TO_USD } from '../../../../common/src/utils/definitions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { EmptyState } from '../ui';

const MarketTodayWidget = () => {
  const { t } = useTranslation('dashboard');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    return [lastWeek, today];
  });
  const [loadingDate, setLoadingDate] = useState(true);
  const [selectedCurrency] = useState<'RMB' | 'USD'>('RMB');
  const [priceView] = useState<string>('kg');
  const [filteredData, setFilteredData] = useState<MarketData[]>([]);
  const [topExportersData, setTopExportersData] = useState<MarketData[]>([]);
  const [marketData, setMarketData] = useState<MarketData[]>([]);

  // Fetch last available date when species or season changes
  useEffect(() => {
    if (!selectedSpecies?.id || !selectedSeason) {
      setMarketData([]);
      setLoadingDate(false);
      return;
    }

    const fetchMarketToday = async () => {
      setLoadingDate(true);
      try {
        const data = await getDashboardMarketToday(
          selectedSpecies.id,
          selectedSeason,
          7
        );

        if (data?.start_date && data?.end_date) {
          const startDate = new Date(`${data.start_date}T12:00:00.000Z`);
          const endDate = new Date(`${data.end_date}T12:00:00.000Z`);
          setDateRange([startDate, endDate]);
        }

        setMarketData(data?.rows || []);
      } catch (error) {
        console.error('Error fetching dashboard market today:', error);
        setMarketData([]);
      } finally {
        setLoadingDate(false);
      }
    };

    fetchMarketToday();
  }, [selectedSpecies?.id, selectedSeason]);

  // Filter data
  useEffect(() => {
    const filterData = (
      data: MarketData[],
      currency: 'RMB' | 'USD'
    ): MarketData[] => {
      const validPrices = data.filter(
        (item) => item.price_kg !== null && item.price_kg > 0
      );

      const multiplier =
        priceView === 'kg' ? 1 : parseInt(priceView.split('-')[1]);

      return validPrices.map((item: MarketData) => ({
        ...item,
        price_kg:
          (currency === 'USD' ? RMB_TO_USD(item.price_kg) : item.price_kg) *
          multiplier,
      }));
    };

    const filtered = filterData(marketData, selectedCurrency);
    setFilteredData(filtered);

    // Get top exporters data (simplified - just take all data for now)
    setTopExportersData(filtered);
  }, [marketData, selectedCurrency, priceView]);

  if (loadingDate) {
    return (
      <div className='space-y-4 p-4'>
        <Skeleton className='h-8 w-1/3 rounded-lg' />
        <Skeleton className='h-64 w-full rounded-lg' />
      </div>
    );
  }

  if (marketData.length === 0) {
    return (
      <div className='flex h-full p-6'>
        <EmptyState
          className='flex-1 justify-center'
          icon='tabler:chart-dots'
          title={t('No hay datos disponibles para los últimos 7 días')}
        />
      </div>
    );
  }

  // Calculate days difference for label
  const daysDifference = Math.ceil(
    (dateRange[1].getTime() - dateRange[0].getTime()) / (1000 * 60 * 60 * 24)
  );
  const dateRangeLabel = `${t('últimos')} ${daysDifference} ${t('días')}`;

  return (
    <VarietyPriceChart
      data={filteredData}
      currency={selectedCurrency}
      priceView={priceView as 'kg' | 'box'}
      topExportersData={topExportersData}
      boxSize={5}
      dateRangeLabel={dateRangeLabel}
      padding='p-4'
      showTopExporters={false}
      compactLayout={true}
    />
  );
};

export default MarketTodayWidget;
